import { useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectTwoFactorMaskedDestination,
  selectAuthStatus,
  selectAuthError,
  selectRegistrationOrganizationId,
} from '@/store/selectors/auth';
import { confirmEmailThunk } from '@/store/thunks/auth/confirmEmailThunk';
import { confirmPhoneThunk } from '@/store/thunks/auth/confirmPhoneThunk';
import { sendEmailCodeThunk } from '@/store/thunks/auth/sendEmailCodeThunk';
import { resendEmailCodeThunk } from '@/store/thunks/auth/resendEmailCodeThunk';
import { useCodeConfirmation } from '@/hooks/shared/useCodeConfirmation';
import { setRegistrationStep } from '@/utils/sessionStorage';
import { AUTO_PHONE_VERIFICATION_ORGANIZATION_ID, AUTO_PHONE_VERIFICATION_CODE } from '@/constants';

interface UseEmailConfirmationState {
  code: string;
  hasSubmitted: boolean;
  isResending: boolean;
  resendCountdown: number;
  codeError: string | undefined;
  isSubmitting: boolean;
  isVerifyDisabled: boolean;
  resendDisabled: boolean;
  maskedEmail: string;
  codeLength: number;
  globalError: string | undefined;
}

interface UseEmailConfirmationHandlers {
  handleCodeChange: (next: string) => void;
  handleSubmit: () => void;
  handleResend: () => void;
}

interface UseEmailConfirmationReturn {
  state: UseEmailConfirmationState;
  handlers: UseEmailConfirmationHandlers;
}

export function useEmailConfirmation(): UseEmailConfirmationReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const maskedDestination = useAppSelector(selectTwoFactorMaskedDestination);
  const authStatus = useAppSelector(selectAuthStatus);
  const authError = useAppSelector(selectAuthError);
  const organizationId = useAppSelector(selectRegistrationOrganizationId);
  const isPhoneVerificationAutomatic = organizationId === AUTO_PHONE_VERIFICATION_ORGANIZATION_ID;

  useEffect(() => {
    void dispatch(sendEmailCodeThunk());
  }, [dispatch]);

  const handleConfirm = useCallback(
    async (code: string): Promise<boolean> => {
      const action = await dispatch(confirmEmailThunk({ code }));
      if (!confirmEmailThunk.fulfilled.match(action)) {
        return false;
      }
      if (isPhoneVerificationAutomatic) {
        const phoneAction = await dispatch(
          confirmPhoneThunk({ code: AUTO_PHONE_VERIFICATION_CODE })
        );
        if (!confirmPhoneThunk.fulfilled.match(phoneAction)) {
          return false;
        }
        setRegistrationStep('password');
        await navigate({ to: '/registration/set-password' });
        return true;
      }
      setRegistrationStep('phoneCode');
      await navigate({ to: '/registration/verify-phone' });
      return true;
    },
    [dispatch, navigate, isPhoneVerificationAutomatic]
  );

  const handleResend = useCallback(async (): Promise<number | undefined> => {
    const action = await dispatch(resendEmailCodeThunk());
    if (resendEmailCodeThunk.fulfilled.match(action)) {
      return action.payload.expiresInSeconds;
    }
    return undefined;
  }, [dispatch]);

  const { state, handlers } = useCodeConfirmation({
    onConfirm: handleConfirm,
    onResend: handleResend,
    maskedDestination: maskedDestination ?? '',
    loading: authStatus === 'loading',
    error: authError,
  });

  return {
    state: {
      ...state,
      maskedEmail: state.maskedDestination,
      isVerifyDisabled: state.isSubmitDisabled,
    },
    handlers,
  };
}
